"use client";
import { H3 } from "@expo/html-elements";
import { createAccordion } from "@gluestack-ui/core/accordion/creator";
import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import {
  tva,
  useStyleContext,
  withStyleContext,
} from "@gluestack-ui/utils/nativewind-utils";
import { cssInterop } from "nativewind";
import React from "react";
import { Platform, Pressable, Text, TextProps, View } from "react-native";

const SCOPE = "ACCORDION";
/** Styles */

const accordionStyle = tva({
  base: "w-full",
  variants: {
    variant: {
      filled: "",
      unfilled: "",
      bordered: "p-[1px]",
    },
    size: {
      xs: "",
      sm: "",
      md: "",
      lg: "",
    },
    space: {
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-4",
      xl: "gap-5",
    },
    rounded: {
      sm: "",
      md: "",
      lg: "",
      xl: "",
      "2xl": "",
      "3xl": "",
      "4xl": "",
    },
    action: {
      white: "",
    },
  },
});

const accordionItemStyle = tva({
  base: "",
  parentVariants: {
    variant: {
      filled: "bg-background-50",
      unfilled: "bg-transparent",
      bordered:
        "bg-background-100 rounded-md outline-1 outline-outline-300 outline",
    },
    rounded: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
      "3xl": "rounded-3xl",
      "4xl": "rounded-4xl",
    },
    action: {
      white: "bg-background-0",
    },
  },
});
const accordionTitleTextStyle = tva({
  base: "text-typography-900 font-bold flex-1 text-left font-heading",
  parentVariants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
});
const accordionIconStyle = tva({
  base: "text-typography-900 fill-none",
  parentVariants: {
    size: {
      "2xs": "h-3 w-3",
      xs: "h-3.5 w-3.5",
      sm: "h-4 w-4",
      md: "h-[18px] w-[18px]",
      lg: "h-5 w-5",
      xl: "h-6 w-6",
    },
  },
});
const accordionContentTextStyle = tva({
  base: "text-typography-700 font-normal",
  parentVariants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
});
const accordionHeaderStyle = tva({
  base: "mx-0 my-0",
});
const accordionContentStyle = tva({
  base: "px-5 mt-2 pb-5",
  parentVariants: {
    variant: {
      filled: "",
      unfilled: "",
      bordered: "bg-background-0 mt-0",
    },
    rounded: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
      "3xl": "rounded-3xl",
      "4xl": "rounded-4xl",
    },
    size: {
      xs: "px-2",
      sm: "px-3",
      md: "px-5",
      lg: "px-6",
    },
  },
});
const accordionTriggerStyle = tva({
  base: "w-full py-6 px-6 flex-row justify-between items-center web:outline-none focus:outline-none data-[disabled=true]:opacity-40 data-[focus-visible=true]:bg-background-50",
  parentVariants: {
    size: {
      xs: "py-2 px-2",
      sm: "py-3 px-3",
      md: "py-5 px-5",
      lg: "py-6 px-6",
    },
  },
});

const Root = withStyleContext(View, SCOPE);

const Header = (
  Platform.OS === "web" ? H3 : View
) as React.ComponentType<TextProps>;

/** Creator */
const UIAccordion = createAccordion({
  Root: Root,
  Item: View,
  Header: Header,
  Trigger: Pressable,
  Icon: UIIcon,
  TitleText: Text,
  ContentText: Text,
  Content: View,
});

cssInterop(PrimitiveIcon, {
  className: {
    target: "style",
    nativeStyleToProp: {
      height: true,
      width: true,
      fill: true,
      color: "classNameColor",
      stroke: true,
    },
  },
});

cssInterop(H3, {
  className: {
    target: "style",
  },
});

type IAccordionProps = React.ComponentPropsWithoutRef<typeof UIAccordion> &
  VariantProps<typeof accordionStyle>;

type IAccordionItemProps = React.ComponentPropsWithoutRef<
  typeof UIAccordion.Item
> &
  VariantProps<typeof accordionItemStyle>;

type IAccordionContentProps = React.ComponentPropsWithoutRef<
  typeof UIAccordion.Content
> &
  VariantProps<typeof accordionContentStyle>;

type IAccordionContentTextProps = React.ComponentPropsWithoutRef<
  typeof UIAccordion.ContentText
> &
  VariantProps<typeof accordionContentTextStyle>;

type IAccordionIconProps = VariantProps<typeof accordionIconStyle> &
  React.ComponentPropsWithoutRef<typeof UIAccordion.Icon> & {
    as?: React.ElementType;
    height?: number;
    width?: number;
  };

type IAccordionHeaderProps = React.ComponentPropsWithoutRef<
  typeof UIAccordion.Header
> &
  VariantProps<typeof accordionHeaderStyle>;

type AccordionTriggerRenderState = {
  hovered: boolean;
  focused: boolean;
  pressed: boolean;
  disabled: boolean;
  focusVisible: boolean;
  isExpanded: boolean;
};

type IAccordionTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof UIAccordion.Trigger>,
  "children"
> &
  VariantProps<typeof accordionTriggerStyle> & {
    children?:
      | React.ReactNode
      | ((state: AccordionTriggerRenderState) => React.ReactNode);
  };

type IAccordionTitleTextProps = React.ComponentPropsWithoutRef<
  typeof UIAccordion.TitleText
> &
  VariantProps<typeof accordionTitleTextStyle>;

/** Components */

const Accordion = React.forwardRef<
  React.ComponentRef<typeof UIAccordion>,
  IAccordionProps
>(
  (
    {
      className,
      variant = "filled",
      size = "md",
      space,
      rounded,
      action,
      ...props
    },
    ref,
  ) => {
    return (
      <UIAccordion
        ref={ref}
        {...props}
        className={accordionStyle({
          variant,
          class: className,
          space,
          rounded,
          action,
        })}
        context={{ variant, size, rounded, action }}
      />
    );
  },
);

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.Item>,
  IAccordionItemProps
>(({ className, ...props }, ref) => {
  const { variant, rounded, action } = useStyleContext(SCOPE);
  return (
    <UIAccordion.Item
      ref={ref}
      {...props}
      className={accordionItemStyle({
        parentVariants: { variant, rounded, action },
        class: className,
      })}
    />
  );
});

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.Content>,
  IAccordionContentProps
>(function AccordionContent({ className, ...props }, ref) {
  const { variant, rounded, size } = useStyleContext(SCOPE);
  return (
    <UIAccordion.Content
      ref={ref}
      {...props}
      className={accordionContentStyle({
        class: className,
        parentVariants: {
          variant,
          rounded,
          size,
        },
      })}
    />
  );
});

const AccordionContentText = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.ContentText>,
  IAccordionContentTextProps
>(function AccordionContentText({ className, ...props }, ref) {
  const { size } = useStyleContext(SCOPE);
  return (
    <UIAccordion.ContentText
      ref={ref}
      {...props}
      className={accordionContentTextStyle({
        parentVariants: { size },
        class: className,
      })}
    />
  );
});

const AccordionIcon = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.Icon>,
  IAccordionIconProps
>(function AccordionIcon({ size, className, ...props }, ref) {
  const { size: parentSize } = useStyleContext(SCOPE);

  if (typeof size === "number") {
    return (
      <UIAccordion.Icon
        ref={ref}
        {...props}
        className={accordionIconStyle({ class: className })}
        size={size}
      />
    );
  } else if (
    (props.height !== undefined || props.width !== undefined) &&
    size === undefined
  ) {
    return (
      <UIAccordion.Icon
        ref={ref}
        {...props}
        className={accordionIconStyle({ class: className })}
      />
    );
  }
  return (
    <UIAccordion.Icon
      ref={ref}
      {...props}
      className={accordionIconStyle({
        size,
        class: className,
        parentVariants: { size: parentSize },
      })}
    />
  );
});

const AccordionHeader = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.Header>,
  IAccordionHeaderProps
>(function AccordionHeader({ className, ...props }, ref) {
  return (
    <UIAccordion.Header
      ref={ref}
      {...props}
      className={accordionHeaderStyle({
        class: className,
      })}
    />
  );
});

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.Trigger>,
  IAccordionTriggerProps
>(function AccordionTrigger({ className, children, ...props }, ref) {
  const { size } = useStyleContext(SCOPE);
  return (
    <UIAccordion.Trigger
      ref={ref}
      {...props}
      className={accordionTriggerStyle({
        class: className,
        parentVariants: { size },
      })}
    >
      {children as React.ReactNode}
    </UIAccordion.Trigger>
  );
});
const AccordionTitleText = React.forwardRef<
  React.ComponentRef<typeof UIAccordion.TitleText>,
  IAccordionTitleTextProps
>(function AccordionTitleText({ className, ...props }, ref) {
  const { size } = useStyleContext(SCOPE);
  return (
    <UIAccordion.TitleText
      ref={ref}
      {...props}
      className={accordionTitleTextStyle({
        parentVariants: { size },
        class: className,
      })}
    />
  );
});

Accordion.displayName = "Accordion";
AccordionItem.displayName = "AccordionItem";
AccordionHeader.displayName = "AccordionHeader";
AccordionTrigger.displayName = "AccordionTrigger";
AccordionTitleText.displayName = "AccordionTitleText";
AccordionContentText.displayName = "AccordionContentText";
AccordionIcon.displayName = "AccordionIcon";
AccordionContent.displayName = "AccordionContent";

export {
  Accordion,
  AccordionContent,
  AccordionContentText,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionTitleText,
  AccordionTrigger,
};
